import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Alert, Button, Skeleton } from 'antd'
import { BriefcaseBusiness, Building2, Plus, ReceiptText } from 'lucide-react'
import { z } from 'zod'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  BillList,
  billSearchSchema,
  DEFAULT_ORDER_GROUP_BILL_SEARCH,
  orderGroupBillQuery
} from '@/features/bill/index.ts'
import {
  OrderGroupIncludeModal,
  OrderGroupUpdateModal,
  orderListSearchSchema,
  rentalListSearchSchema,
  RentalOrderList,
  ServiceOrderList
} from '@/features/order/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { cn } from '@/utils/index.ts'

export const Route = createFileRoute('/_authenticated/orders/groups/$groupId')({
  beforeLoad: ({ params }) => {
    if (!Number.isSafeInteger(Number(params.groupId)) || Number(params.groupId) < 1) {
      throw redirect({ to: '/orders/groups', search: { current: 1, pageSize: 20 } })
    }
  },
  validateSearch: z.object({
    kind: z.enum(['rental', 'service', 'bills']).catch('rental'),
    bills: billSearchSchema.optional().catch(undefined),
    rental: rentalListSearchSchema.optional().catch(undefined),
    service: orderListSearchSchema.optional().catch(undefined)
  }),
  head: () => ({ meta: [{ title: '订单组详情 · Super Mega' }] }),
  component: OrderGroupPage
})

function OrderGroupPage() {
  const access = useAccess()
  const { currentSession } = Route.useRouteContext()
  const groupId = Number(Route.useParams().groupId)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const trpc = useTRPC()
  const query = useQuery(trpc.order.groups.overview.queryOptions({ orderGroupId: groupId }))
  const [editing, setEditing] = useState<'customer' | 'salesperson' | 'contractStatus' | 'include'>()
  // 列表将查询对象作为选择会话标识；保留引用，避免打开弹窗时清空已选订单。
  const rentalSearch = useMemo(
    () => ({ ...rentalListSearchSchema.parse(search.rental ?? {}), orderGroupId: groupId }),
    [search.rental, groupId]
  )
  const serviceSearch = useMemo(
    () => ({ ...orderListSearchSchema.parse(search.service ?? {}), orderGroupId: groupId }),
    [search.service, groupId]
  )
  const canReadBills = currentSession.isSuperAdmin || currentSession.permissions.includes('bill.read')
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('order.group.') && !permission.endsWith('.read')
    )
  const canManageContracts =
    canManage ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('contract.document.') && !permission.endsWith('.read')
    )
  const group = query.data
  return (
    <div className="bg-page text-content flex min-h-dvh flex-col">
      <WorkspaceHeader className="sticky top-0 z-10">
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <h1 className="m-0 truncate text-base font-semibold">
              {group ? `${group.name} · ${group.customerName}` : '订单组详情'}
            </h1>
            <p className="text-content-muted m-0 text-xs">
              ID：{groupId}
              {group ? ` · ${group.orderCount} 个订单` : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <>
              <Authorized permissions={['order.group.update']}>
                <Button disabled={!group || query.isError} onClick={() => setEditing('customer')}>
                  一键修改客户
                </Button>
              </Authorized>
              <Authorized permissions={['order.group.update']}>
                <Button
                  disabled={!group || query.isError || !group.orderCount}
                  onClick={() => setEditing('salesperson')}
                >
                  一键修改业务员
                </Button>
              </Authorized>
            </>
          )}
          {canManageContracts && (
            <Button
              disabled={!group || query.isError || !group.orderCount}
              onClick={() => setEditing('contractStatus')}
            >
              一键修改合同状态
            </Button>
          )}
          {canManage && (
            <Button
              type="primary"
              icon={<Plus aria-hidden size={16} />}
              disabled={!group || query.isError}
              onClick={() => setEditing('include')}
            >
              纳入订单组
            </Button>
          )}
        </div>
      </WorkspaceHeader>
      {query.isError ? (
        <Alert
          className="m-4"
          type="error"
          showIcon
          title={getErrorMessage(query.error, '订单组加载失败')}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      ) : !group ? (
        <Skeleton active className="p-6" />
      ) : (
        <div className="grid min-w-0 flex-1 grid-cols-1 items-start gap-4 p-4 lg:grid-cols-[12rem_minmax(0,1fr)]">
          <nav aria-label="订单分类" className="bg-surface flex gap-2 rounded-xl p-3 lg:sticky lg:top-24 lg:flex-col">
            {(
              [
                { key: 'rental', label: '租赁订单', count: group.rentalOrderCount, Icon: Building2 },
                { key: 'service', label: '服务订单', count: group.serviceOrderCount, Icon: BriefcaseBusiness },
                ...(canReadBills
                  ? [{ key: 'bills' as const, label: '收款账单', count: undefined, Icon: ReceiptText }]
                  : [])
              ] as const
            ).map(({ key, label, count, Icon }) => (
              <button
                key={key}
                type="button"
                aria-current={search.kind === key ? 'page' : undefined}
                onClick={() => void navigate({ search: (previous) => ({ ...previous, kind: key }) })}
                className={cn(
                  'flex flex-1 cursor-pointer items-center gap-2 rounded-lg border-0 px-3 py-3 text-left text-sm',
                  search.kind === key
                    ? 'bg-accent/10 text-accent font-medium'
                    : 'bg-surface text-content hover:bg-surface-muted'
                )}
              >
                <Icon aria-hidden size={18} />
                <span className="flex-1">{label}</span>
                <span>{count}</span>
              </button>
            ))}
          </nav>
          <main className="bg-surface min-w-0 overflow-hidden rounded-xl">
            {!access.can(
              ({ rental: 'order.rental.read', service: 'order.service.read', bills: 'bill.read' } as const)[search.kind]
            ) ? (
              <Alert type="info" title="没有此类订单或账单的读取权限" />
            ) : search.kind === 'rental' ? (
              <RentalOrderList
                key={`rental-${groupId}`}
                compact
                currentSession={currentSession}
                search={rentalSearch}
                navigateSearch={(options) =>
                  navigate({
                    replace: options.replace,
                    search: (previous) => ({
                      ...previous,
                      rental: {
                        ...(typeof options.search === 'function' ? options.search(rentalSearch) : options.search),
                        orderGroupId: groupId
                      }
                    })
                  })
                }
              />
            ) : search.kind === 'bills' ? (
              <OrderGroupBills key={groupId} />
            ) : (
              <ServiceOrderList
                key={`service-${groupId}`}
                compact
                currentSession={currentSession}
                search={serviceSearch}
                navigateSearch={(options) =>
                  navigate({
                    replace: options.replace,
                    search: (previous) => ({
                      ...previous,
                      service: {
                        ...(typeof options.search === 'function' ? options.search(serviceSearch) : options.search),
                        orderGroupId: groupId
                      }
                    })
                  })
                }
              />
            )}
          </main>
        </div>
      )}
      {group && editing === 'include' && (
        <OrderGroupIncludeModal key={groupId} group={group} onClose={() => setEditing(undefined)} />
      )}
      {group && editing && editing !== 'include' && (
        <OrderGroupUpdateModal
          key={`${groupId}-${editing}`}
          group={group}
          kind={editing}
          onClose={() => setEditing(undefined)}
        />
      )}
    </div>
  )
}

function OrderGroupBills() {
  const { currentSession } = Route.useRouteContext()
  const groupId = Number(Route.useParams().groupId)
  const raw = Route.useSearch().bills ?? DEFAULT_ORDER_GROUP_BILL_SEARCH
  const navigate = Route.useNavigate()
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: orderGroupBillQuery,
    raw,
    accountId: currentSession.id,
    navigate: (options) =>
      navigate({
        replace: options.replace,
        search: (previous) => ({
          ...previous,
          bills: {
            ...billSearchSchema.parse(
              typeof options.search === 'function'
                ? options.search(previous.bills ?? DEFAULT_ORDER_GROUP_BILL_SEARCH)
                : options.search
            ),
            orderGroupId: groupId,
            direction: 'receivable'
          }
        })
      })
  })
  return (
    <BillList
      currentSession={currentSession}
      search={{ ...search, orderGroupId: groupId, direction: 'receivable' }}
      defaultSearch={{ ...DEFAULT_ORDER_GROUP_BILL_SEARCH, orderGroupId: groupId }}
      navigateSearch={navigateSearch}
    />
  )
}
